import type { BattleEvent, BattleReport, BattleResult, BattleSummary } from "../../../shared/types/BattleEvent";
import type { CommanderProfile } from "../../../shared/types/CommanderProfile";
import type { GameCommand } from "../../../shared/types/GameCommand";
import type { PlayerMemory } from "../../../shared/types/PlayerMemory";
import type { ClientToServerMessage, ServerToClientMessage } from "../../../shared/types/Protocol";
import type { Language } from "../i18n";

export type CommanderConnectionStatus = "connecting" | "connected" | "healthy" | "offline" | "misconfigured" | "health-failed" | "recovering" | "error";

type CommanderSocketCallbacks = {
  onCommanderCreated?: (profile: CommanderProfile) => void;
  onTaunt?: (taunt: string, mood: string) => void;
  onCommand?: (command: GameCommand) => void;
  onReport?: (report: BattleReport) => void;
  onStatus?: (status: CommanderConnectionStatus) => void;
  onChat?: (reply: string, mood: string) => void;
};

type ResolvedCommanderUrl = {
  url?: string;
  healthUrl?: string;
  probeBeforeConnect?: boolean;
};

export class CommanderSocket {
  private socket?: WebSocket;
  private connected = false;
  private reconnectAttempts = 0;
  private reconnectTimer?: number;
  private connection?: {
    playerId: string;
    commanderProfile: CommanderProfile | undefined;
    playerMemory: PlayerMemory;
    language: Language;
  };

  constructor(private readonly callbacks: CommanderSocketCallbacks) {}

  connect(playerId: string, commanderProfile: CommanderProfile | undefined, playerMemory: PlayerMemory, language: Language) {
    this.connection = { playerId, commanderProfile, playerMemory, language };
    this.openSocket();
  }

  private openSocket() {
    const resolved = this.resolveCommanderUrl();
    if (!resolved.url) {
      this.connected = false;
      this.callbacks.onStatus?.("misconfigured");
      return;
    }
    if (!this.connection) return;

    if (resolved.probeBeforeConnect && resolved.healthUrl) {
      this.callbacks.onStatus?.("connecting");
      this.probeHealth(resolved.healthUrl).then((healthy) => {
        if (!healthy) {
          this.connected = false;
          this.callbacks.onStatus?.("health-failed");
          return;
        }
        this.openResolvedSocket(resolved);
      });
      return;
    }

    this.openResolvedSocket(resolved);
  }

  private openResolvedSocket(resolved: ResolvedCommanderUrl) {
    if (!resolved.url || !this.connection) return;

    // Drop any previous socket so its listeners cannot fire after we move on
    // (prevents listener accumulation and stale reconnect loops across retries).
    this.teardownSocket();

    try {
      this.callbacks.onStatus?.("connecting");
      const socket = new WebSocket(resolved.url);
      this.socket = socket;
      socket.addEventListener("open", () => {
        if (this.socket !== socket) return;
        this.connected = true;
        this.reconnectAttempts = 0;
        this.clearReconnectTimer();
        this.callbacks.onStatus?.("connected");
        this.checkHealth(resolved.healthUrl);
        this.send({
          type: "HELLO",
          playerId: this.connection!.playerId,
          commanderProfile: this.connection!.commanderProfile,
          playerMemory: this.connection!.playerMemory,
          language: this.connection!.language
        });
      });
      socket.addEventListener("message", (event) => {
        if (this.socket !== socket) return;
        this.handleMessage(event.data);
      });
      socket.addEventListener("close", () => {
        if (this.socket !== socket) return;
        this.handleDisconnect();
      });
      socket.addEventListener("error", () => {
        if (this.socket !== socket) return;
        this.handleDisconnect();
      });
    } catch {
      this.handleDisconnect();
    }
  }

  private teardownSocket() {
    const socket = this.socket;
    if (!socket) return;
    this.socket = undefined;
    try {
      socket.close();
    } catch {
      // Ignore errors closing an already-dead socket.
    }
  }

  sendEvents(events: BattleEvent[]) {
    if (events.length > 0) {
      this.send({ type: "BATTLE_EVENTS", events });
    }
  }

  sendSummary(summary: BattleSummary) {
    this.send({ type: "BATTLE_SUMMARY", summary });
  }

  requestDecision(summary: BattleSummary) {
    this.send({ type: "REQUEST_COMMANDER_DECISION", summary });
  }

  sendBattleEnded(result: BattleResult) {
    this.send({ type: "BATTLE_ENDED", result });
  }

  sendChat(text: string, language: Language, summary: BattleSummary) {
    const trimmed = text.trim();
    if (!trimmed) return;
    this.send({ type: "PLAYER_CHAT", text: trimmed.slice(0, 220), language, summary });
  }

  isConnected() {
    return this.connected;
  }

  private send(message: ClientToServerMessage) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  private handleDisconnect() {
    if (!this.connected && this.reconnectTimer !== undefined) return;
    this.connected = false;
    this.callbacks.onStatus?.("offline");
    this.scheduleReconnect();
  }

  private scheduleReconnect() {
    if (!this.connection || this.reconnectAttempts >= 3 || this.reconnectTimer !== undefined) return;
    this.reconnectAttempts += 1;
    this.callbacks.onStatus?.("recovering");
    const delayMs = 900 * this.reconnectAttempts;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.openSocket();
    }, delayMs);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer === undefined) return;
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
  }

  private resolveCommanderUrl(): ResolvedCommanderUrl {
    const configuredUrl = (import.meta.env.VITE_COMMANDER_WS_URL as string | undefined)?.trim();
    if (configuredUrl) {
      return {
        url: configuredUrl,
        healthUrl: configuredUrl.replace(/^wss:/, "https:").replace(/^ws:/, "http:").replace(/\/ws\/?$/, "/health")
      };
    }

    const hostName = window.location.hostname || "127.0.0.1";
    const isLocalHost = hostName === "localhost" || hostName === "127.0.0.1" || hostName === "::1";
    if (import.meta.env.DEV || isLocalHost) {
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      return {
        url: `${protocol}://${hostName}:8787/ws`,
        healthUrl: `${window.location.protocol}//${hostName}:8787/health`
      };
    }

    const sameOriginProtocol = window.location.protocol === "https:" ? "wss" : "ws";
    return {
      url: `${sameOriginProtocol}://${window.location.host}/ws`,
      healthUrl: `${window.location.origin}/health`,
      probeBeforeConnect: true
    };
  }

  private checkHealth(healthUrl?: string) {
    if (!healthUrl) return;
    this.probeHealth(healthUrl)
      .then((healthy) => this.callbacks.onStatus?.(healthy ? "healthy" : "health-failed"))
      .catch(() => this.callbacks.onStatus?.("health-failed"));
  }

  private async probeHealth(healthUrl: string) {
    const response = await fetch(healthUrl);
    if (!response.ok) return false;
    const text = await response.text();
    if (!text.trim()) return true;
    try {
      const body = JSON.parse(text) as { ok?: boolean; service?: string };
      return body.ok !== false && typeof body.service === "string";
    } catch {
      return false;
    }
  }

  private handleMessage(raw: string) {
    let message: ServerToClientMessage;
    try {
      message = JSON.parse(raw) as ServerToClientMessage;
    } catch {
      return;
    }

    switch (message.type) {
      case "COMMANDER_CREATED":
        this.callbacks.onCommanderCreated?.(message.profile);
        break;
      case "COMMANDER_TAUNT":
        this.callbacks.onTaunt?.(message.taunt, message.mood);
        break;
      case "COMMANDER_CHAT":
        this.callbacks.onChat?.(message.reply, message.mood);
        break;
      case "GAME_COMMAND":
        this.callbacks.onCommand?.(message.command);
        break;
      case "BATTLE_REPORT":
        this.callbacks.onReport?.(message.report);
        break;
      case "AI_ERROR":
        this.callbacks.onStatus?.(message.recoverable ? "recovering" : "error");
        break;
    }
  }
}
